import { useEffect, useState } from 'react';
import localforage from 'localforage';
import { useGetSceneAllParagraphs } from '../../../domains/scene/ui/hooks/useGetSceneAllParagraphs';
import type { SceneAllParagraphsResponse } from '../../../domains/scene/services/sceneService';

const STORAGE_KEY_PREFIX = 'sceneAllParagraphs_';

export function useSceneAllParagraphsWithIndexedDB(sceneId: string) {
  const [indexedDBData, setIndexedDBData] = useState<SceneAllParagraphsResponse | null>(null);
  const [indexedDBCheckDone, setIndexedDBCheckDone] = useState(false);

  const shouldFetchFromApi = !!sceneId && indexedDBCheckDone && !indexedDBData;
  const { data: apiData, isLoading: isApiLoading } = useGetSceneAllParagraphs(sceneId, {
    enabled: shouldFetchFromApi
  });

  useEffect(() => {
    if (!sceneId) return;

    const key = `${STORAGE_KEY_PREFIX}${sceneId}`;
    const loadFromIndexedDB = async () => {
      try {
        const cached = await localforage.getItem<SceneAllParagraphsResponse>(key);
        setIndexedDBCheckDone(true);
        setIndexedDBData(cached ?? null);
      } catch {
        setIndexedDBCheckDone(true);
        setIndexedDBData(null);
      }
    };
    loadFromIndexedDB();
  }, [sceneId]);

  useEffect(() => {
    if (!sceneId || !apiData || indexedDBData != null) return;

    const key = `${STORAGE_KEY_PREFIX}${sceneId}`;
    const saveToIndexedDB = async () => {
      try {
        await localforage.setItem(key, apiData);
      } catch {
        // Ignore save errors; API data is still available
      }
    };
    saveToIndexedDB();
  }, [sceneId, apiData, indexedDBData]);

  const data = indexedDBData ?? apiData;
  const isLoading = !indexedDBCheckDone || (indexedDBData != null ? false : isApiLoading);

  return { data, isLoading };
}
